import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent, type ReactNode } from 'react'
import { ApiError } from '../../api/client'
import {
  ABUSEIPDB_KEY,
  abuseIpdbDelete,
  abuseIpdbPageQuery,
  abuseIpdbSave,
  abuseIpdbTest,
  type AbuseIpdbPage,
  type AbuseIpdbSetting,
} from '../../api/settingsAbuseIpdb'
import { AbuseIpdbActivity, blockTime } from './AbuseIpdbActivity'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import './abuse-ipdb.css'

/**
 * Settings > Securitee > AbuseIPDB: cheia AbuseIPDB a firmei, pornit/oprit si
 * lista alba. Pornit, ph4 verifica IP-ul din detectiile de brute force si il
 * blocheaza pe aparatul atacat (regulile in `AbuseIpdbSettings`, API).
 */

type FieldError = { field: string; message: string }

function errorOf(err: unknown): FieldError {
  const details = err instanceof ApiError ? err.details : undefined
  return { field: typeof details?.field === 'string' ? details.field : '', message: err instanceof Error ? err.message : 'Something went wrong.' }
}

function keyStatus(s: AbuseIpdbSetting): ReactNode {
  if (s.check_ok === true) return <span className="label label-success">Key works{s.checks_left !== null ? ` · ${s.checks_left} checks left today` : ''}</span>
  if (s.check_ok === false) return <span className="label label-danger">Failed: {s.check_error ?? 'no answer'}</span>
  return <span className="label label-default">Not tested</span>
}

export function AbuseIpdbPanel() {
  const client = useQueryClient()
  const page = useQuery(abuseIpdbPageQuery())

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!page.data) return <Spinner />
  const data = page.data
  const show = (next: AbuseIpdbPage) => client.setQueryData(ABUSEIPDB_KEY, next)

  return (
    <div className="abuseipdb-panel">
      <p className="help-block abuseipdb-intro">
        When this is on, the source IP of every failed logon or brute force from a public address is checked in AbuseIPDB, once a day per IP and
        device. Its confidence score decides what happens on the
        attacked device; a block is lifted at the device&apos;s next check-in after its time. Below {data.tiers[0]?.from ?? 20}% nothing happens.
        The incident shows the block.
      </p>
      <table className="table table-condensed abuseipdb-tiers" aria-label="What happens at each AbuseIPDB score">
        <thead>
          <tr>
            <th scope="col">Score</th>
            <th scope="col">What happens</th>
          </tr>
        </thead>
        <tbody>
          {data.tiers.map((t) => (
            <tr key={t.from}>
              <td>
                {t.from}–{t.to}%
              </td>
              <td>{t.minutes === null ? 'Noted only' : `Blocked ${blockTime(t.minutes)}`}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="abuseipdb-rules">
        <strong>Checked on:</strong> {data.rules.map((r) => r.label).join(' · ')}
      </p>
      {!data.cryptoReady && <div className="alert alert-warning">API keys cannot be stored: encryption is not configured on the server (DEFUSE_CRYPT_PASS).</div>}
      {!data.ready && <div className="alert alert-warning">The AbuseIPDB tables are missing. The database migrations must be run first.</div>}
      <SettingForm key={JSON.stringify(data.setting)} data={data} onPage={show} onTested={() => void client.invalidateQueries({ queryKey: ABUSEIPDB_KEY })} />
      {data.setting ? <AbuseIpdbActivity /> : null}
    </div>
  )
}

/*
 * O alegere din trei in locul a doua bife (Vasile, 23 octombrie): "Report only"
 * fara "Block automatically" nu facea nimic. In API raman `enabled` + `report_only`.
 */
type Mode = 'off' | 'watch' | 'block'

const MODES: { value: Mode; label: string; help: string }[] = [
  { value: 'off', label: 'Off', help: 'Nothing is checked.' },
  {
    value: 'watch',
    label: "Watch only (don't block)",
    help: 'Each attacker IP is checked and noted with what would have happened, for example "Would block 30 min"; nothing is blocked. Good for trying AbuseIPDB on a company first. Only detections from after you turn this on are checked.',
  },
  {
    value: 'block',
    label: 'Block automatically',
    help: 'Attacker IPs are blocked on the attacked device, for the time of their tier above. Only detections from after you turn this on are checked.',
  },
]

function SettingForm({ data, onPage, onTested }: { data: AbuseIpdbPage; onPage: (p: AbuseIpdbPage) => void; onTested: () => void }) {
  const saved = data.setting
  const [apiKey, setApiKey] = useState('')
  const [mode, setMode] = useState<Mode>(!saved?.enabled ? 'off' : saved.report_only ? 'watch' : 'block')
  const [reporting, setReporting] = useState(saved?.report_enabled ?? false)
  const [allowlist, setAllowlist] = useState(saved?.allowlist ?? '')
  const [error, setError] = useState<FieldError | null>(null)
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const readOnly = !data.canWrite

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      onPage(await abuseIpdbSave({ api_key: apiKey, enabled: mode !== 'off', report_only: mode === 'watch', report_enabled: reporting, allowlist }))
      toast('AbuseIPDB settings saved.', 'Saved', 'success')
    } catch (err) {
      setError(errorOf(err))
    } finally {
      setBusy(false)
    }
  }

  async function test() {
    setBusy(true)
    try {
      // raspunsul se vede in eticheta cheii, din pagina recitita
      await abuseIpdbTest()
      onTested()
    } catch (err) {
      toast(errorOf(err).message, 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    setBusy(true)
    try {
      onPage(await abuseIpdbDelete())
    } catch (err) {
      toast(errorOf(err).message, 'Error', 'error')
      setBusy(false)
    }
  }

  const fieldError = (field: string) => (error?.field === field ? error.message : '')
  const general = error && !['api_key', 'allowlist'].includes(error.field) ? error.message : ''

  return (
    <form className="abuseipdb-form" noValidate onSubmit={(e) => void submit(e)}>
      {saved && (
        <div className="abuseipdb-status">
          {saved.enabled ? <span className="label label-success">On since {saved.enabled_at} UTC</span> : <span className="label label-default">Off</span>}
          {saved.enabled && saved.report_only && <span className="label label-warning">Watch only: nothing is blocked</span>}
          {saved.paused_until && <span className="label label-warning">Paused until {saved.paused_until} UTC (daily AbuseIPDB limit reached)</span>}
          {keyStatus(saved)}
        </div>
      )}

      <div className={'form-group' + (fieldError('api_key') ? ' has-error' : '')}>
        <label htmlFor="abuseipdb-key">API key</label>
        <input
          id="abuseipdb-key"
          className="form-control"
          type="password"
          autoComplete="new-password"
          maxLength={200}
          value={apiKey}
          disabled={readOnly || busy}
          placeholder={saved ? 'Saved. Leave empty to keep it.' : ''}
          aria-invalid={fieldError('api_key') ? true : undefined}
          aria-describedby="abuseipdb-key-help"
          onChange={(e) => setApiKey(e.target.value)}
        />
        <span className="help-block" id="abuseipdb-key-help">
          {fieldError('api_key') || 'abuseipdb.com › Account › API. The free plan allows 1,000 checks a day. Stored encrypted; it is never shown again.'}
        </span>
      </div>

      <fieldset className="form-group abuseipdb-mode" role="radiogroup" aria-labelledby="abuseipdb-mode-legend" aria-describedby="abuseipdb-mode-help">
        <legend id="abuseipdb-mode-legend">Automatic response</legend>
        {MODES.map((m) => (
          <div className="radio" key={m.value}>
            <label>
              <input type="radio" name="abuseipdb-mode" value={m.value} checked={mode === m.value} disabled={readOnly || busy} onChange={() => setMode(m.value)} />
              {m.label}
            </label>
          </div>
        ))}
        <span className="help-block" id="abuseipdb-mode-help">
          {MODES.find((m) => m.value === mode)?.help}
        </span>
      </fieldset>

      <div className="checkbox">
        <label>
          <input
            type="checkbox"
            checked={reporting}
            disabled={readOnly || busy}
            onChange={(e) => setReporting(e.target.checked)}
            aria-describedby="abuseipdb-report-help"
          />
          Report attackers to AbuseIPDB
        </label>
        <span className="help-block" id="abuseipdb-report-help">
          Attacker IPs with a score of 20% or more are reported as brute force, at most once a day per IP. Reports are public on abuseipdb.com and are
          made with this company&apos;s key; they say only what was seen, nothing about the company or its devices.
        </span>
      </div>

      <div className={'form-group' + (fieldError('allowlist') ? ' has-error' : '')}>
        <label htmlFor="abuseipdb-allowlist">Never block (one IP or network per line)</label>
        <textarea
          id="abuseipdb-allowlist"
          className="form-control abuseipdb-allowlist"
          rows={4}
          value={allowlist}
          disabled={readOnly || busy}
          aria-invalid={fieldError('allowlist') ? true : undefined}
          aria-describedby="abuseipdb-allowlist-help"
          onChange={(e) => setAllowlist(e.target.value)}
        />
        <span className="help-block" id="abuseipdb-allowlist-help">
          {fieldError('allowlist') || "For example 203.0.113.7 or 198.51.100.0/24. The company's office IPs and the public IPs of its own devices are never blocked anyway."}
        </span>
      </div>

      {general !== '' && <div className="alert alert-danger">{general}</div>}

      {!readOnly && (
        <div className="abuseipdb-buttons">
          <button type="submit" className="btn btn-primary" disabled={busy}>
            Save
          </button>
          {saved && (
            <button type="button" className="btn btn-default" disabled={busy} onClick={() => void test()}>
              Test key
            </button>
          )}
          {saved && !confirming && (
            <button type="button" className="btn btn-link abuseipdb-remove" disabled={busy} onClick={() => setConfirming(true)}>
              Remove
            </button>
          )}
        </div>
      )}
      {confirming && (
        <div className="alert alert-warning abuseipdb-confirm" role="alertdialog" aria-label="Remove the AbuseIPDB key">
          <span>Remove the key? Automatic blocking stops. Blocks already made are still lifted when their time runs out.</span>
          <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={() => void remove()}>
            Yes, remove
          </button>
          <button type="button" className="btn btn-default btn-sm" disabled={busy} onClick={() => setConfirming(false)}>
            Cancel
          </button>
        </div>
      )}
    </form>
  )
}
